import { getServices } from '~/db/services';
import { retrieveMediaMapFromIds } from '~/lib/media';
import type { AssessmentListItem } from '~/types/ui';
import { getDaysLeft } from '~/lib/utils/date';

export const useAssessmentsList = (uuids: string[] = null) => {
  const { $db } = useNuxtApp();
  const { categoriesService, exerciseInfosService } = getServices($db);
  const assessmentsStore = useAssessmentsStore();

  const assessments = computed(() => {
    if (!uuids) {
      return assessmentsStore.assessments;
    }
    return assessmentsStore.assessments.reduce((acc, assessment) => {
      if (uuids.includes(assessment.uuid)) {
        acc.push(assessment);
      }
      return acc;
    }, []);
  });

  const items = ref<AssessmentListItem[]>([]);

  const mProgressionByChapter =
    ref<Map<string, { chapterId: string; total: number; count: number }>>();

  const load = async () => {
    const idsAndLbs = [];

    assessments.value.forEach((assessment) => {
      idsAndLbs.push([assessment.disciplineId, assessment.libraryBookId]);
      assessment.chapterIds.forEach((id) => {
        idsAndLbs.push([id, assessment.libraryBookId]);
      });
    });
    const categories = await categoriesService.findByIdsAndLibraryBookIds(
      idsAndLbs
    );
    const mediaIcons: string[] = [];
    const mCategories = new Map<string, CategorySchema>();

    categories.forEach((cat) => {
      mCategories.set(cat.id, cat);
      if (cat.contentType === ContentTypes.Discipline && cat.mediaIcon) {
        mediaIcons.push(cat.mediaIcon);
      }
    });
    const mMedia = await retrieveMediaMapFromIds($db, mediaIcons);

    let _allParamsChapterIds: string[][] = [];
    const _items: AssessmentListItem[] = [];
    assessments.value.forEach((assessment) => {
      if (mCategories.has(assessment.disciplineId)) {
        const { title, mediaIcon } = mCategories.get(assessment.disciplineId);
        const chapters = [];
        const imgSrc =
          mediaIcon && mMedia.has(mediaIcon)
            ? mMedia.get(mediaIcon).urlCompressed || mMedia.get(mediaIcon).url
            : null;
        const paramsChapters = assessment.chapterIds.map((chapterId) => [
          assessment.libraryBookId,
          chapterId,
          assessment.member,
        ]);
        _allParamsChapterIds = [..._allParamsChapterIds, ...paramsChapters];
        assessment.chapterIds.forEach((chapterId) => {
          if (mCategories.has(chapterId)) {
            chapters.push({
              title: mCategories.get(chapterId).title,
              id: chapterId,
            });
          }
        });
        const daysLeft = getDaysLeft(assessment.date);
        if (chapters.length) {
          _items.push({
            ...assessment,
            title,
            chapters,
            imgSrc,
            daysLeft,
            formatedDate: getFormatedDateCalendar(assessment.date),
            clockTime: getClockTime(assessment.date),
            timeLeft: getTimeLeft(assessment.date),
            ended: getTimeLeft(assessment.date) < 0,
            count: 0,
            total: 1,
          });
        }
      }
    });

    const exerciseInfos =
      await exerciseInfosService.findByLibraryBookIdChapterIdAndMemberIDParams(
        _allParamsChapterIds
      );

    mProgressionByChapter.value = new Map();
    // const mProgressionByChapter = new Map<
    //   string,
    //   { chapterId: string; total: number; count: number }
    // >();

    exerciseInfos.forEach((exerciseInfo) => {
      if (!mProgressionByChapter.value.has(exerciseInfo.chapterId)) {
        mProgressionByChapter.value.set(exerciseInfo.chapterId, {
          chapterId: exerciseInfo.chapterId,
          count: 0,
          total: 0,
        });
      }
      mProgressionByChapter.value.get(exerciseInfo.chapterId).count +=
        exerciseInfo.isFinished;
      mProgressionByChapter.value.get(exerciseInfo.chapterId).total += 1;
    });

    _items.forEach((item) => {
      let count: number = 0;
      let total: number = 0;
      item.chapterIds.forEach((chapterId) => {
        if (mProgressionByChapter.value.has(chapterId)) {
          const result = mProgressionByChapter.value.get(chapterId);
          count += result.count;
          total += result.total;
        }
      });
      item.count = count;
      item.total = total > 0 ? total : 1;
    });

    let _itemsEnded = _items.filter((item) => item.ended);
    let _itemsRunning = _items.filter((item) => !item.ended);

    _itemsEnded = _itemsEnded.sort((a, b) => {
      return -new Date(a.date).getTime() + new Date(b.date).getTime();
    });
    _itemsRunning = _itemsRunning.sort((a, b) => {
      return new Date(a.date).getTime() - new Date(b.date).getTime();
    });
    items.value = [..._itemsRunning, ..._itemsEnded];
  };
  return {
    load,
    items,
    mProgressionByChapter,
  };
};
